import { useEffect, useState } from "react";
import { Alert, Button, DatePicker, Form, Input, Modal, Select, Space, Table, Tag, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { PageHeader } from "../../components/PageHeader";
import { useApi } from "../../helpers/useApi";
import type { ApprovalDelegation, Role, User } from "../../api-models";

const DOC_TYPES = [
  "BOM_VERSION",
  "BOM_VARIATION",
  "MRQ",
  "TRANSFER_ORDER",
  "SITE_RETURN",
  "SUPPLIER_RETURN",
  "CONSUMPTION",
  "STOCK_ADJUSTMENT_SITE",
  "STOCK_ADJUSTMENT_WAREHOUSE",
  "PR",
  "PO",
  "RFQ",
  "PO_AMENDMENT",
  "ATTENDANCE_SHEET",
];

// Delegation hands one user's approval authority to another for a bounded window
// (spec §25 "time-bound and audited"). It never widens authority: the delegate can only
// act where the delegator could have.
export default function DelegationsPage() {
  const { request, loading } = useApi();
  const [rows, setRows] = useState<ApprovalDelegation[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  async function load() {
    setRows(await request<ApprovalDelegation[]>({ url: "/approval-delegations" }));
  }

  useEffect(() => {
    load();
    request<{ data: User[] }>({ url: "/users", params: { pageSize: 200 } }).then((p) => setUsers(p.data ?? []));
    request<Role[]>({ url: "/roles" }).then(setRoles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function create() {
    const values = await form.validateFields();
    await request({
      method: "POST",
      url: "/approval-delegations",
      data: {
        fromUserId: values.fromUserId,
        toUserId: values.toUserId,
        roleId: values.roleId ?? null,
        docType: values.docType ?? null,
        fromAt: values.window[0].toISOString(),
        toAt: values.window[1].toISOString(),
        reason: values.reason,
      },
    });
    message.success("Delegation created.");
    setOpen(false);
    form.resetFields();
    load();
  }

  async function revoke(id: number) {
    await request({ method: "POST", url: `/approval-delegations/${id}/revoke` });
    message.success("Delegation revoked.");
    load();
  }

  function statusOf(d: ApprovalDelegation) {
    if (d.revokedAt) return <Tag color="red">Revoked</Tag>;
    const now = Date.now();
    if (new Date(d.toAt).getTime() < now) return <Tag>Expired</Tag>;
    if (new Date(d.fromAt).getTime() > now) return <Tag color="blue">Scheduled</Tag>;
    return <Tag color="green">Active</Tag>;
  }

  return (
    <div>
      <PageHeader
        title="Approval Delegations"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
            New delegation
          </Button>
        }
      />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Delegation is time-bound and audited"
        description="Within the window, the delegate can act on approvals the delegator could have acted on — optionally narrowed to a single role or document type. Revoking takes effect immediately."
      />
      <Table
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={false}
        columns={[
          { title: "From", render: (_, r) => r.fromUser?.name ?? r.fromUser?.email ?? r.fromUserId },
          { title: "To", render: (_, r) => r.toUser?.name ?? r.toUser?.email ?? r.toUserId },
          { title: "Role", render: (_, r) => r.role?.name ?? "Any role held" },
          { title: "Document type", render: (_, r) => r.docType ?? "All" },
          { title: "From", render: (_, r) => new Date(r.fromAt).toLocaleDateString() },
          { title: "Until", render: (_, r) => new Date(r.toAt).toLocaleDateString() },
          { title: "Reason", dataIndex: "reason", ellipsis: true },
          { title: "Status", render: (_, r) => statusOf(r) },
          {
            title: "",
            render: (_, r) =>
              !r.revokedAt && (
                <Button size="small" danger onClick={() => revoke(r.id)}>
                  Revoke
                </Button>
              ),
          },
        ]}
      />

      <Modal title="New delegation" open={open} onCancel={() => setOpen(false)} onOk={create} confirmLoading={loading} destroyOnHidden>
        <Form form={form} layout="vertical">
          <Form.Item name="fromUserId" label="Delegating from" extra="Leave empty to delegate your own authority.">
            <Select allowClear showSearch optionFilterProp="label" options={users.map((u) => ({ value: u.id, label: u.name || u.email }))} />
          </Form.Item>
          <Form.Item name="toUserId" label="Delegate to" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label" options={users.map((u) => ({ value: u.id, label: u.name || u.email }))} />
          </Form.Item>
          <Form.Item name="window" label="Window" rules={[{ required: true }]}>
            <DatePicker.RangePicker style={{ width: "100%" }} />
          </Form.Item>
          <Space size="middle" style={{ display: "flex" }}>
            <Form.Item name="roleId" label="Limit to role" style={{ flex: 1 }}>
              <Select allowClear options={roles.map((r) => ({ value: r.id, label: r.name }))} placeholder="Any role" />
            </Form.Item>
            <Form.Item name="docType" label="Limit to document type" style={{ flex: 1 }}>
              <Select allowClear options={DOC_TYPES.map((d) => ({ value: d, label: d.replace(/_/g, " ") }))} placeholder="All types" />
            </Form.Item>
          </Space>
          <Form.Item name="reason" label="Reason" rules={[{ required: true }]}>
            <Input.TextArea rows={2} placeholder="e.g. Annual leave 12–20 August" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
